import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import BaseDialog from 'core/components/base-dialog';
import { FormField } from 'core/components/form';
import { useStyles } from '../../car-assignment.styles';

const NONE_ID = 'NONE';

const MACarAssignmentModal = ({ open, onClose, subtitle, drivers, isSenior }) => {
  const classes = useStyles();
  const { values, setFieldTouched, setFieldValue } = useFormikContext();

  const carsOptions = useMemo(() => {
    const cars = values.cars.map((car) => ({
      id: car.VIN,
      value: `${car.year || ''} ${car.make || ''} ${car.model || `New Car ${car.VIN}`}`
    }));
    if (!isSenior) {
      return [...cars, { id: NONE_ID, value: 'They are not the primary driver for any of these vehicles.' }];
    }

    return cars;
  }, [isSenior, values.cars]);

  // because of the way the validations works each field must have been touched
  useEffect(() => {
    values.drivers.forEach((_driver, index) => {
      setFieldTouched(`drivers[${index}].userAssignedDriver`);
    });
  }, []);

  const handleChange = useCallback(
    (value, name) => {
      const driverIndex = +name.substring(name.indexOf('[') + 1, name.lastIndexOf(']'));
      const { id } = values.drivers[driverIndex];
      const carIndex = values.cars.findIndex((car) =>
        value !== NONE_ID ? car.VIN === value : car.userAssignedDriver === id
      );
      setFieldValue(`cars[${carIndex}].userAssignedDriver`, value !== NONE_ID ? id : ' ');
      // we need to remove the previously assigned car
      if (value) {
        const previousCarIndex = values.cars.findIndex((car) => car.VIN !== value && car.userAssignedDriver === id);
        setFieldValue(`cars[${previousCarIndex}].userAssignedDriver`, ' ');
      }
    },
    [setFieldValue, drivers, values.cars]
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={
        <div className={classes.assignDriversTitle}>
          <Label type="greenBig">Assign Drivers to Cars</Label>
          <Label type="infoSubtitle" className={classes.assignDriversSubtitle}>
            {subtitle}
          </Label>
        </div>
      }
    >
      <div className={classes.modal}>
        {drivers.map((driver) => {
          const index = values.drivers.findIndex((d) => d.id === driver.id);
          return (
            <FormField
              id={`drivers[${index}].userAssignedCar`}
              name={`drivers[${index}].userAssignedCar`}
              type="select"
              options={carsOptions}
              mode="light"
              fast={false}
              label={`Is ${driver.firstName} ${driver.lastName} the main driver of any of these vehicles?`}
              key={driver.id}
              onChange={handleChange}
            />
          );
        })}
        <div className={classes.assignDriversFooter}>
          <Button
            variant="contained"
            onClick={onClose}
            color="secondary"
            type="submit"
            className={classes.footerButton}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

MACarAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subtitle: PropTypes.string.isRequired,
  drivers: PropTypes.array.isRequired,
  isSenior: PropTypes.bool
};

MACarAssignmentModal.defaultProps = {
  isSenior: false
};

export default MACarAssignmentModal;
